import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { HippoApiService } from 'services/hippoApi';

export const module = angular.module('experimentsService', []);

interface Variation {
    variationId: string;
    variationName: string;
}

type ExperimentId = string;

interface Experiment {
    experimentId: string;
    experimentName: string;
    true: Variation;
    false: Variation;
    distribution: number;
    isEnabled: boolean;
    ticket: string;
    notes: string;
}

interface CookieOptions {
    secure: boolean;
    domain: string;
    expires: Date;
}

interface Flags {
    [experimentId: string]: string;
}

interface CookieData {
    flags: Flags;
    cookie_time: number;
    referrer: string;
    experiments?: { [experimentId: string]: boolean };
    previous_cookies?: Array<CookieData>;
}

export interface Experiments {
    [experimentId: string]: Experiment | undefined;
}

interface ICookiesService {
    get(key: string): string;
    getObject(key: string): any;
    getAll(): any;
    put(key: string, value: string, options?: any): void;
    putObject(key: string, value: any, options?: any): void;
    remove(key: string, options?: any): void;
}

const COOKIE_NAME_EXPERIMENTS = 'experiments';
const COOKIE_NAME_FLAGS = 'hippo-flags';
const MAX_COOKIE_SIZE = 2045;

export class ExperimentsService {
    $cookies: ICookiesService;
    $window: angular.IWindowService;
    $location: angular.ILocationService;
    experiments: Experiments;
    $log: angular.ILogService;
    hippoApiService: HippoApiService;

    constructor(
        hippoApiService: HippoApiService,
        $cookies: ICookiesService,
        $window: angular.IWindowService,
        $location: angular.ILocationService,
        $log: angular.ILogService
    ) {
        this.$cookies = $cookies;
        this.$location = $location;
        this.$window = $window;
        this.experiments = {};
        this.$log = $log;
        this.hippoApiService = hippoApiService;

        try {
            const cookieOptions = this._getCookieOptions();
            const flags = this.getFlags();
            hippoApiService.getExperiments().then((result) => (this.experiments = result));
            this._setCookies(flags, cookieOptions);
        } catch (e) {
            this.$log.warn('unable to get experiment data', e);
        }
    }

    getFlags() {
        const queryStringWithoutQuestionMark = this.$window.location.search
            ? this.$window.location.search.substring(1)
            : '';
        let queryParameters = queryStringWithoutQuestionMark.split('&');

        if (this.$location.absUrl().indexOf('#') !== -1) {
            // To handle params after hash without since $location.hash doesn't work all the time
            const queryParamsAfterHashWithoutQuestionOrSlash = this.$location
                .url()
                .replace('?', '')
                .replace('/', '');

            queryParameters = queryParameters.concat(queryParamsAfterHashWithoutQuestionOrSlash.split('&'));
        }

        let flags = queryParameters.reduce(function(result: { [key: string]: string }, item) {
            if (item.indexOf('=') > 0) {
                const parameterName = item.split('=')[0]; // because we know it exists due to check above
                const parameterValue = item.split('=')[1];

                result[decodeURIComponent(parameterName)] = decodeURIComponent(parameterValue);
            }

            return result;
        }, {});

        // keep previous flags, override with new flags.
        flags = {
            ...this._getCookieFlags(),
            ...flags
        };

        return flags;
    }

    getExperiment(experimentId: ExperimentId) {
        try {
            const experiments = this.$cookies.getObject(COOKIE_NAME_FLAGS)[COOKIE_NAME_EXPERIMENTS];

            if (!experiments) {
                return false;
            }

            return _.mapKeys(experiments, (value: any, key: string) => key.toLowerCase())[experimentId.toLowerCase()];
        } catch (e) {
            this.$log.warn('unable to get experiement', e);
            return null;
        }
    }

    isExperimentEnabled(experimentId: ExperimentId) {
        try {
            const flags: Flags = this.getFlags();
            const { distribution = 50, isEnabled = null } = this.experiments[experimentId.toLowerCase()] || {};
            if (flags[experimentId] === 'false') {
                return false;
            } else if (flags[experimentId] === 'true') {
                return true;
            } else if (flags.disableExperiments === 'true') {
                return false;
            } else if (isEnabled === true && distribution === 100) {
                return true;
            } else if (isEnabled === false) {
                return false;
            } else {
                return this.getExperiment(experimentId) || false;
            }
        } catch (e) {
            this.$log.warn('unable to check if experiment is enabled', e);
            return true;
        }
    }

    isLocalHost() {
        const locationHostName = this._getLocationHostName();
        return locationHostName === '127.0.0.1' || locationHostName.toLowerCase().indexOf('localhost') === 0;
    }

    getDomainName() {
        return this._getLocationHostName()
            .split('.')
            .slice(-2)
            .join('.');
    }

    _getLocationHostName() {
        return this.$window.location.hostname;
    }

    _getCookieOptions() {
        const cookieOptions: CookieOptions = {
            secure: !this.isLocalHost(),
            domain: this.getDomainName(),
            expires: moment()
                .add(1, 'year')
                .toDate()
        };

        return cookieOptions;
    }

    _isExperimentEnabled(experimentKey: string) {
        const { isEnabled = false, distribution = 50 } = this.experiments[experimentKey] || {};
        const distributionPrct = distribution / 100;
        if (!isEnabled) {
            return false;
        }
        return Math.random() < distributionPrct;
    }

    _setupExperiments(experiments: { [experimentId: string]: Experiment | undefined }) {
        const experimentCookieData: { [experimentId: string]: boolean } = {};
        for (const experimentKey of Object.keys(experiments)) {
            const experiment = experiments[experimentKey] as Experiment; // because we know the key exists
            const isExperimentEnabled = this._isExperimentEnabled(experimentKey);
            experimentCookieData[experiment.experimentId] = isExperimentEnabled;
        }
        return experimentCookieData;
    }

    _getCookieFlags() {
        const previousHippoFlagCookie = this.$cookies.get(COOKIE_NAME_FLAGS);
        if (previousHippoFlagCookie) {
            const hippoFlagCookie: CookieData = JSON.parse(this.$cookies.get(COOKIE_NAME_FLAGS));
            return hippoFlagCookie.flags;
        }

        return null;
    }

    // tslint:disable-next-line:cognitive-complexity
    _setCookies(flags: Flags, cookieOptions: CookieOptions) {
        let cookieData = {} as CookieData;

        if ((flags && Object.keys(flags).length) || document.referrer) {
            cookieData = {
                flags,
                cookie_time: Date.now() / 1000,
                referrer: document.referrer
            };

            const previousCookieString = this.$cookies.get(COOKIE_NAME_FLAGS);

            if (previousCookieString) {
                const previousCookie: CookieData = JSON.parse(previousCookieString);
                cookieData.previous_cookies = [];
                const previousCookies = previousCookie.previous_cookies;
                cookieData.flags = {
                    ...previousCookie.flags,
                    ...cookieData.flags
                };
                delete previousCookie.previous_cookies;

                cookieData.experiments = previousCookie.experiments;

                // Only add previous cookies to new cookie if we have some storage space to spare.
                // We have to fight for every byte (bite) of the cookie!
                if (
                    previousCookie &&
                    JSON.stringify(cookieData).length + JSON.stringify(previousCookie).length < MAX_COOKIE_SIZE
                ) {
                    cookieData.previous_cookies.push(previousCookie);

                    if (previousCookies && previousCookies.length) {
                        for (let i = 0; i < previousCookies.length; i++) {
                            if (
                                JSON.stringify(cookieData).length + JSON.stringify(previousCookies[i]).length >=
                                MAX_COOKIE_SIZE
                            ) {
                                break;
                            }

                            cookieData.previous_cookies.push(previousCookies[i]);
                        }
                    }
                }
            }
        }

        cookieData.experiments = this._setupExperiments(this.experiments);

        if (cookieData && Object.keys(cookieData).length) {
            this.$cookies.put(COOKIE_NAME_FLAGS, JSON.stringify(cookieData), cookieOptions);
        }
    }
}

module.factory('experimentsService', function(
    hippoApiService: HippoApiService,
    $cookies: ICookiesService,
    $window: angular.IWindowService,
    $location: angular.ILocationService,
    $log: angular.ILogService
) {
    'ngInject';
    return new ExperimentsService(hippoApiService, $cookies, $window, $location, $log);
});
